/**
 * Social wire shapes (plan 27 wave 3, lane BM; plan 23 rows 34 and 40).
 *
 * Inspect: the client sends `inspectPlayer { sessionId, requestId }` for a
 * player in its own room; the server answers only that client with
 * `inspectResult`. Leaderboards: two public, cached GET endpoints.
 *
 * Every shape here is PUBLIC data: what other players could already learn
 * by looking at a character. Never gold, bags, bank, quests, account ids,
 * character ids or session ids — the inspect reply carries the requester's
 * own `requestId` for correlation instead of echoing the session id.
 */
import type { EquipmentSchemaSlot } from '../equipmentSlots';

export const INSPECT_REQUEST_MESSAGE = 'inspectPlayer';
export const INSPECT_RESULT_MESSAGE = 'inspectResult';

export interface InspectPlayerRequest {
  /** The other player's session id in the requester's room. */
  sessionId: string;
  /** Client-chosen correlation number, echoed back. */
  requestId: number;
}

/** Totals as the server computes them (base + gear + talents). */
export interface InspectStats {
  maxHealth: number;
  maxMana: number;
  strength: number;
  dexterity: number;
  intelligence: number;
  luck: number;
  attack: number;
  magicAttack: number;
  defense: number;
  magicDefense: number;
  criticalRate: number;
  speed: number;
}

export const INSPECT_STAT_KEYS: readonly (keyof InspectStats)[] = [
  'maxHealth', 'maxMana', 'strength', 'dexterity', 'intelligence', 'luck',
  'attack', 'magicAttack', 'defense', 'magicDefense', 'criticalRate', 'speed',
];

export interface InspectSheet {
  name: string;
  /** heroClass — the calling. */
  calling: string;
  /** Job advancement, '' when none. */
  job: string;
  level: number;
  /** Guild name as shown overhead, '' when none. */
  guildName: string;
  /** Worn item ids by schema slot; empty slots are omitted. */
  equipment: Partial<Record<EquipmentSchemaSlot, string>>;
  stats: InspectStats;
}

export type InspectFailure = 'not_found' | 'rate_limited' | 'invalid';

export type InspectResult =
  | { requestId: number; ok: true; sheet: InspectSheet }
  | { requestId: number; ok: false; reason: InspectFailure };

export const LEADERBOARD_TOP_PATH = '/leaderboard/characters';
export const LEADERBOARD_ONLINE_PATH = '/leaderboard/online';

export interface LeaderboardEntry {
  /** Competition rank: characters with the same level and XP share one. */
  rank: number;
  name: string;
  calling: string;
  level: number;
  xp: number;
  /** Linked Twitch display name or login when authenticated via Twitch. */
  twitch?: string;
}

export interface LeaderboardTopResponse {
  generatedAt: number;
  entries: LeaderboardEntry[];
}

export interface OnlinePlayerEntry {
  name: string;
  calling: string;
  level: number;
  /** The map's display name (or id). */
  zone: string;
  /** Linked Twitch display name or login when authenticated via Twitch. */
  twitch?: string;
}

export interface LeaderboardOnlineResponse {
  generatedAt: number;
  /** Real players online across this server's rooms (bots excluded). */
  total: number;
  players: OnlinePlayerEntry[];
}
